import { useState } from 'react';
import { IonButton, IonInput, IonNote, IonSpinner } from '@ionic/react';
import { Card, Page } from '../components/Shell';
import { AppUpdate } from '../components/AppUpdate';
import {
  candidateServerUrls,
  getServerBase,
  needsServerAddress,
  setServerBase,
} from '../lib/server';
import { getMapApiKey, mapKeyRequired, rememberMapConfig, setMapApiKey } from '../lib/map';
import { getDeviceName, setDeviceName } from '../lib/device';

/**
 * Die Einstellungen: Wo steht der Server, welcher API-Key gilt für die Karte -
 * und ist die App noch aktuell?
 *
 * Die Serveradresse braucht nur die verpackte App (im Browser kommt die
 * Oberfläche vom Server selbst). Der API-Key dagegen liegt immer auf dem
 * Gerät, deshalb ist diese Seite überall erreichbar - in der App genauso wie
 * im Browser.
 *
 * Die Adresse wird nicht blind übernommen, sondern gegen `/api/config`
 * geprüft. Und wenn das schiefgeht, soll dastehen *warum* - ein Tippfehler,
 * ein nicht erreichbarer Server und eine abgelehnte Herkunft sind drei sehr
 * verschiedene Probleme, sehen in JavaScript aber alle gleich aus.
 *
 * Dieselbe Antwort verrät nebenbei, ob der Server den Karten-Schlüssel schon
 * selbst mitbringt (`MAP_API_KEY` im Docker). Dann ist das Feld hier nur noch
 * eine Rückfallebene und sagt das auch.
 *
 * Zuletzt die Version: in der Android-App steht hier, welcher Build läuft und
 * ob es einen neueren gibt (siehe components/AppUpdate.tsx). Im Browser
 * blendet sich die Karte aus - dort ist ein Neuladen das ganze Update.
 */

interface Props {
  onDone: () => void;
  onCancel?: () => void;
}

/** Was genau ist schiefgegangen? */
async function describeFailure(base: string): Promise<string> {
  // `fetch` wirft bei einem Netzfehler denselben TypeError wie bei einer
  // CORS-Ablehnung. Ein Versuch mit `no-cors` trennt die beiden Fälle:
  // kommt er durch, ist der Server erreichbar und lehnt nur die App ab.
  try {
    await fetch(`${base}/api/config`, { mode: 'no-cors' });
    return (
      'Der Server antwortet, erlaubt dieser App aber den Zugriff nicht (CORS). ' +
      'Das behebt ein Update des Servers auf den aktuellen Stand - ältere ' +
      'Versionen kennen die App noch nicht.'
    );
  } catch {
    return (
      'Der Server ist unter dieser Adresse nicht erreichbar. Stimmt sie, und ' +
      'ist sie vom Handy aus offen? Bei HTTPS muss das Zertifikat gültig sein.'
    );
  }
}

export function ServerSetup({ onDone, onCancel }: Props) {
  const [driverName, setDriverNameInput] = useState(() => getDeviceName());
  const [address, setAddress] = useState(getServerBase());
  const [mapKey, setMapKey] = useState(getMapApiKey());
  const [showKey, setShowKey] = useState(false);
  const [checking, setChecking] = useState(false);
  const [error, setError] = useState<string | null>(null);

  /** Prüft eine Adresse. Gibt null zurück, wenn sie passt, sonst den Grund. */
  async function check(base: string): Promise<string | null> {
    let response: Response;
    try {
      response = await fetch(`${base}/api/config`);
    } catch {
      return await describeFailure(base);
    }

    if (!response.ok) {
      return (
        `Unter dieser Adresse antwortet etwas, aber /api/config meldet Status ` +
        `${response.status}. Läuft dort wirklich der R2F-Server?`
      );
    }

    try {
      // Verrät auch, welche Kartenkacheln dieser Server vorgibt.
      rememberMapConfig(JSON.parse(await response.text())?.map);
    } catch {
      return (
        'Unter dieser Adresse läuft etwas anderes als der R2F-Server - ' +
        '/api/config hat kein JSON geliefert.'
      );
    }
    return null;
  }

  async function connect(): Promise<void> {
    // Der Karten-Schlüssel hängt nicht am Server: er gilt auch dann, wenn die
    // Adresse gleich daneben scheitert.
    setMapApiKey(mapKey);

    // Im Browser gibt es keine Adresse zu prüfen - der API-Key allein ist
    // schon die ganze Einstellung.
    if (!needsServerAddress()) {
      onDone();
      return;
    }

    const candidates = candidateServerUrls(address);
    if (candidates.length === 0) {
      setError('Bitte die Adresse des Servers eintragen.');
      return;
    }

    setChecking(true);
    setError(null);
    try {
      let lastReason: string | null = null;
      for (const base of candidates) {
        const reason = await check(base);
        if (reason === null) {
          setServerBase(base);
          onDone();
          return;
        }
        lastReason = reason;
      }
      setError(lastReason);
    } finally {
      setChecking(false);
    }
  }

  return (
    <Page title="Einstellungen" onBack={onCancel}>
      <Card>
        <h2>Dein Name</h2>
        <p>So siehst du für Mitfahrer und Kontakte aus - in Benachrichtigungen und beim Verbinden.</p>
        <IonInput
          label="Dein Name"
          labelPlacement="stacked"
          placeholder="z. B. Lena"
          maxlength={40}
          value={driverName}
          onIonInput={(event) => setDriverNameInput(event.detail.value ?? '')}
          onIonBlur={() => setDeviceName(driverName)}
        />
      </Card>

      {needsServerAddress() && (
        <Card>
          <h2>Wo steht dein Server?</h2>
          <p>
            Die App bringt nur die Oberfläche mit. Damit Fahrten, Kontakte und
            Benachrichtigungen funktionieren, braucht sie einmalig die Adresse
            deines R2F-Servers.
          </p>

          <IonInput
            label="Adresse"
            labelPlacement="stacked"
            placeholder="r2f.example.de"
            inputmode="url"
            autocapitalize="off"
            autocorrect="off"
            value={address}
            onIonInput={(event) => setAddress(String(event.detail.value ?? ''))}
            onKeyDown={(event) => {
              if (event.key === 'Enter') void connect();
            }}
          />

          <IonNote>
            Ohne <code>https://</code> davor wird erst HTTPS probiert, dann HTTP.
            Läuft der Server auf deinem Rechner im selben WLAN, ist es dessen
            lokale IP samt Port (<code>192.168.1.50:8080</code>) - nicht{' '}
            <code>localhost</code>, das wäre das Handy selbst.
          </IonNote>

          {error && <p style={{ color: 'var(--ion-color-danger, #ff6b6b)' }}>{error}</p>}
        </Card>
      )}

      <Card>
        <h2>API-Key für die Karte</h2>
        <p>
          {mapKeyRequired()
            ? 'Die Kacheln deines Servers verlangen einen API-Key, der Server selbst kennt aber keinen. Ohne Eintrag hier zeigen die Karten die Kacheln ohne Key.'
            : 'Dein Server bringt die Karte schon mit (und holt die Kacheln selbst) - nötig ist hier nichts. Ein Eintrag greift, sobald der Server einen Key verlangt.'}
        </p>

        <IonInput
          label="API-Key"
          labelPlacement="stacked"
          placeholder="hier eintragen (optional)"
          type={showKey ? 'text' : 'password'}
          autocapitalize="off"
          autocorrect="off"
          spellcheck={false}
          value={mapKey}
          onIonInput={(event) => setMapKey(String(event.detail.value ?? ''))}
          onKeyDown={(event) => {
            if (event.key === 'Enter') void connect();
          }}
        />

        <IonButton fill="clear" size="small" onClick={() => setShowKey((on) => !on)}>
          {showKey ? 'Key verbergen' : 'Key anzeigen'}
        </IonButton>

        <IonNote>
          Der Key bleibt auf diesem Gerät. Auf dem Server heißt dieselbe
          Einstellung <code>MAP_API_KEY</code> - wer sie dort setzt, spart sich
          den Eintrag auf jedem Handy.
        </IonNote>
      </Card>

      {/* In der App: welche Version laeuft hier, und gibt es eine neuere? */}
      <AppUpdate variant="settings" />

      <IonButton expand="block" size="large" disabled={checking} onClick={() => void connect()}>
        {checking ? (
          <IonSpinner name="dots" />
        ) : needsServerAddress() ? (
          'Speichern und verbinden'
        ) : (
          'Speichern'
        )}
      </IonButton>
    </Page>
  );
}
